import { useToast } from '../components/utils/ToastProvider';

export const useCommonToasts = () => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const toasts = {
    // Watchlist actions
    watchlistCreated: (name: string, undoAction?: () => void) => {
      showSuccess(
        `Your list “${name}” is ready.`,
        5000,
        undoAction ? { label: 'Undo', onClick: undoAction } : undefined
      );
    },
    watchlistDeleted: (name: string) => {
      showSuccess(`“${name}” has been deleted.`, 4000);
    },
    watchlistUpdated: (name: string) => {
      showSuccess(`Saved changes to “${name}”.`, 4000);
    },

    // Movie actions
    movieAddedToWatchlist: (
      title: string, 
      watchlistName: string = "Want to Watch",
      undoAction?: () => void,
      onWatchlistClick?: () => void
    ) => {
      const hasUndo = Boolean(undoAction);
      const hasManage = Boolean(onWatchlistClick);
      showSuccess(
        `Added “${title}” to ${watchlistName}.`,
        hasUndo || hasManage ? 6500 : 5000,
        hasUndo && undoAction ? { label: 'Undo', onClick: undoAction } : undefined,
        hasManage && onWatchlistClick ? { text: watchlistName, onClick: onWatchlistClick } : undefined
      );
    },
    movieRemovedFromWatchlist: (
      title: string,
      listName?: string,
      undoAction?: () => void | Promise<void>
    ) => {
      const place = listName ? `“${listName}”` : 'your list';
      showSuccess(
        `Removed “${title}” from ${place}.`,
        undoAction ? 7000 : 4000,
        undoAction
          ? {
              label: 'Undo',
              onClick: () => {
                void Promise.resolve(undoAction()).catch((err) => {
                  console.error('Undo failed:', err);
                });
              },
            }
          : undefined
      );
    },
    movieMarkedAsWatched: (title: string) => {
      showSuccess(`Nice — you’ve watched “${title}”.`, 4500);
    },
    movieUnmarkedAsWatched: (title: string) => {
      showSuccess(`“${title}” is back on your radar.`, 4500);
    },
    reviewSaved: (title: string) => {
      showSuccess(`Review saved for “${title}”.`, 4000);
    },

    // Error messages
    networkError: () => {
      showError('Network error. Please check your connection.');
    },
    saveError: () => {
      showError('Failed to save changes. Please try again.');
    },
    loadError: () => {
      showError('Failed to load data. Please refresh the page.');
    },
    permissionError: () => {
      showError('You don\'t have permission to perform this action.');
    },

    // Info messages
    comingSoon: (feature: string) => {
      showInfo(`${feature} feature coming soon!`);
    },
    offlineMode: () => {
      showWarning('You\'re offline. Some features may be limited.');
    },
    syncComplete: () => {
      showSuccess('You’re up to date.', 3500);
    },
    
    // Favorite people (stars)
    personAddedToFavorites: (name: string) => {
      showSuccess(`${name} added to favorites ⭐`);
    },
    personRemovedFromFavorites: (name: string) => {
      showSuccess(`${name} removed from favorites`);
    },

    // Authentication
    loginSuccess: () => {
      showSuccess('Welcome back!', 4000);
    },
    logoutSuccess: () => {
      showSuccess('See you next time.', 3500);
    },
    authError: () => {
      showError('Authentication failed. Please try again.');
    },

    // Loading states
    saving: () => {
      showInfo('Saving...', 2000);
    },
    loading: () => {
      showInfo('Loading...', 2000);
    },

    // New feature coming soon
    featureComingSoon: () => {
      showInfo('This feature is coming soon!', 3000);
    },
  };

  return toasts;
};

export default useCommonToasts; 